import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppUser, LineQuota, UserStatus } from '@contracts/auth';
import { Banner, Empty, Panel } from '../components/Panel';
import { useMe } from '../hooks/useMe';
import { api, ApiError } from '../lib/api';
import { dateTime } from '../lib/format';

const GROUPS: { status: UserStatus; title: string }[] = [
  { status: 'pending', title: 'รออนุมัติ' },
  { status: 'approved', title: 'อนุมัติแล้ว' },
  { status: 'disabled', title: 'ปิดใช้งาน' },
];

type Change = Partial<Pick<AppUser, 'status' | 'role' | 'notifyAlerts' | 'notifyOutcomes'>>;

export function AdminUsersPage() {
  const qc = useQueryClient();
  const me = useMe();
  const users = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => api.get<{ users: AppUser[] }>('/api/v1/admin/users'),
    refetchInterval: 20_000,
  });
  const quota = useQuery({ queryKey: ['line-quota'], queryFn: () => api.get<LineQuota>('/api/v1/admin/line/quota') });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['admin-users'] });
  const change = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Change }) => api.post(`/api/v1/admin/users/${id}`, body),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/admin/users/${id}/delete`),
    onSettled: refresh,
  });
  const test = useMutation({ mutationFn: (id: string) => api.post(`/api/v1/admin/users/${id}/test`) });
  const error = change.error ?? remove.error ?? test.error;
  const myId = me.data?.user?.id;

  return (
    <>
      <header className="page__header">
        <h1>ผู้ใช้และผู้รับแจ้งเตือน</h1>
        <p className="page__lede">
          อนุมัติผู้ที่เข้าสู่ระบบด้วย LINE แล้วเลือกว่าใครได้รับแจ้งเตือนสัญญาณและผล TP/SL ทาง LINE
        </p>
      </header>
      {me.data && !me.data.authRequired ? (
        <Banner tone="info" title="Login is disabled on this server (AUTH_REQUIRED=false)">
          ผู้ใช้จะปรากฏเมื่อเปิด Login with LINE บน server จริง
        </Banner>
      ) : null}
      {error ? (
        <Banner tone="danger" title="ทำรายการไม่สำเร็จ">
          {error instanceof ApiError ? error.message : 'Unknown error'}
        </Banner>
      ) : null}
      {test.isSuccess ? <Banner tone="info" title="ส่งข้อความทดสอบแล้ว" /> : null}
      <div className="stack">
        <Panel title="โควตาข้อความ LINE เดือนนี้">
          {quota.data?.available ? (
            <p className="tabular">
              ใช้ไป {quota.data.used ?? '—'} {quota.data.limit ? `/ ${quota.data.limit}` : '(ไม่จำกัด)'} ข้อความ
              <span className="small muted"> · นับต่อผู้รับ: 1 สัญญาณ × 5 คน = 5 ข้อความ</span>
            </p>
          ) : (
            <p className="small muted">{quota.data?.error ?? 'ยังไม่ได้ตั้งค่า LINE_CHANNEL_ACCESS_TOKEN'}</p>
          )}
        </Panel>
        {users.isLoading ? <Empty title="กำลังโหลด…" /> : null}
        {users.isError ? <Empty title="โหลดรายชื่อไม่สำเร็จ" /> : null}
        {users.data
          ? GROUPS.map((g) => {
              const list = users.data.users.filter((u) => u.status === g.status);
              return (
                <Panel key={g.status} title={g.title} meta={`${list.length} คน`}>
                  {list.length === 0 ? (
                    <p className="small muted">ไม่มี</p>
                  ) : (
                    <div className="table-wrap">
                      <table className="ledger">
                        <caption className="visually-hidden">{g.title}</caption>
                        <thead>
                          <tr>
                            <th>ผู้ใช้</th>
                            <th>เพื่อน OA</th>
                            <th>รับสัญญาณ</th>
                            <th>รับผล TP/SL</th>
                            <th>เข้าล่าสุด</th>
                            <th>จัดการ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {list.map((u) => (
                            <tr key={u.id}>
                              <td>
                                <strong>{u.displayName || '—'}</strong>
                                {u.role === 'admin' ? <span className="badge badge--gold">admin</span> : null}
                                <div className="small muted">
                                  <code>{u.lineUserId}</code>
                                </div>
                              </td>
                              <td>{u.isFriend === null ? '—' : u.isFriend ? 'เป็นเพื่อน' : '⚠ ยังไม่เป็นเพื่อน'}</td>
                              <td>
                                <input
                                  type="checkbox"
                                  aria-label={`รับสัญญาณ: ${u.displayName}`}
                                  checked={u.notifyAlerts}
                                  onChange={(e) =>
                                    change.mutate({ id: u.id, body: { notifyAlerts: e.target.checked } })
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="checkbox"
                                  aria-label={`รับผล TP/SL: ${u.displayName}`}
                                  checked={u.notifyOutcomes}
                                  onChange={(e) =>
                                    change.mutate({ id: u.id, body: { notifyOutcomes: e.target.checked } })
                                  }
                                />
                              </td>
                              <td className="small">{dateTime(u.lastLoginAt)}</td>
                              <td>
                                <div className="row" style={{ flexWrap: 'wrap', gap: 4 }}>
                                  {u.status !== 'approved' ? (
                                    <button
                                      type="button"
                                      className="btn btn--primary"
                                      onClick={() => change.mutate({ id: u.id, body: { status: 'approved' } })}
                                    >
                                      อนุมัติ
                                    </button>
                                  ) : null}
                                  {u.status === 'approved' && u.id !== myId ? (
                                    <button
                                      type="button"
                                      className="btn"
                                      onClick={() => change.mutate({ id: u.id, body: { status: 'disabled' } })}
                                    >
                                      ปิดใช้งาน
                                    </button>
                                  ) : null}
                                  {u.status === 'approved' ? (
                                    <button type="button" className="btn" onClick={() => test.mutate(u.id)}>
                                      ส่งทดสอบ
                                    </button>
                                  ) : null}
                                  {u.id !== myId && u.role !== 'admin' ? (
                                    <button
                                      type="button"
                                      className="btn btn--danger"
                                      onClick={() => {
                                        if (window.confirm(`ลบ ${u.displayName || 'ผู้ใช้นี้'}?`)) remove.mutate(u.id);
                                      }}
                                    >
                                      ลบ
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Panel>
              );
            })
          : null}
      </div>
    </>
  );
}
